/**
 * adapters/coverLetterAdapter.js — RC 1.53 Phase 4.3A
 *
 * Thin wrapper around
 * `lib/coverLetterPolling.js::generateCoverLetterWithPolling`.
 *
 * Mirrors `cvVariantAdapter.js` (Phase 4.2). The adapter exists so the
 * section dispatches via the controller (gaining the no-op-on-in-
 * flight guard, lifecycle events, `STABILIZING` status, workspace-
 * wide Generate-lock, and the activity indicator) without changing
 * what the polling helper does on the wire.
 *
 * Strict scope (Phase 4.3A):
 *   • Wraps `generateCoverLetterWithPolling` verbatim — same
 *     `requestBody`, same `apiPost`, same `apiGet`, same
 *     `onRunStatus`, same return shape.
 *   • Does NOT touch idempotency keys (the section mints them).
 *   • Does NOT call any backend endpoint other than the polling
 *     helper (cover letter has NO finalize-charge step — charging
 *     is atomic inside the orchestrator).
 *   • Does NOT write URL params (the section owns URL state).
 *   • Does NOT refresh credits (the section AWAITS
 *     `credits.refresh()` inside the stabilization chain so the
 *     activity indicator stays mounted until the wallet pill is
 *     re-read from server).
 *   • Lazy-imports the polling helper.
 */

export const ACTION_CODE = 'cover_letter';

export async function run({
  requestBody,
  apiPost,
  apiGet,
  onRunStatus,
  // CWS-2 (operator-locked, 2026-02-15) — `onRunIdReady` fires once
  // the dispatch's async_202 envelope returns the runId (which is
  // the letter document id). The section uses this to stamp the
  // inflight marker so mount recovery can poll by id rather than
  // by title-stamp. Sync_200 / replay_200 paths never invoke this.
  onRunIdReady,
  callbacks,
}) {
  if (!requestBody)                       throw new Error('coverLetterAdapter.run: requestBody is required');
  if (typeof apiPost !== 'function')      throw new Error('coverLetterAdapter.run: apiPost must be a function');
  if (typeof apiGet  !== 'function')      throw new Error('coverLetterAdapter.run: apiGet must be a function');

  const { generateCoverLetterWithPolling } = await import(
    /* webpackMode: "eager" */ '@/lib/coverLetterPolling.js'
  );

  // Forward controller callbacks INTO the polling helper's
  // onRunStatus so the controller's operation:progress event fires
  // for every backend-side run_status transition. The section's own
  // `onRunStatus` (toast banners) continues to run; this is
  // additive only.
  const sectionOnRunStatus = onRunStatus;
  const wrappedOnRunStatus = (s) => {
    try {
      if (callbacks?.onProgress) callbacks.onProgress(s, s);
      if (s === 'running' && callbacks?.onPolling) callbacks.onPolling();
    } catch { /* noop — never let callback errors crash polling */ }
    if (typeof sectionOnRunStatus === 'function') {
      try { sectionOnRunStatus(s); } catch { /* noop */ }
    }
  };

  return generateCoverLetterWithPolling({
    requestBody,
    apiPost,
    apiGet,
    onRunStatus: wrappedOnRunStatus,
    onRunIdReady,
  });
}

export const PHASE_4_3A_CONTRACT = Object.freeze({
  wrap_polling_helper:        'lib/coverLetterPolling.js::generateCoverLetterWithPolling',
  url_param_contract:         ['cover_letter_id={id}'],
  inflight_marker_key:        'inflight-cover-letter:${userId}:${jobId}',
  side_effects_owned_by_adapter: [],
  side_effects_owned_by_section: [
    'idempotency_key derivation (getOrMintIdempotencyKey)',
    'inflight marker write/clear (writeInflightLetterMarker / clearInflightLetterMarker)',
    'URL param writes (cover_letter_id) — synchronous on success',
    'cache invalidation (cover-letters:all)',
    'awaited credits.refresh() inside the stabilization chain',
    'lifecycle markComplete after all stabilization lands',
  ],
});
